import React, { useEffect, useMemo, useState } from "react";
import {
  Accordion,
  Box,
  Button,
  Drawer,
  FileButton,
  Group,
  Loader,
  Select,
  Stack,
  Switch,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import {
  IconCircleCheck,
  IconDownload,
  IconExclamationMark,
  IconUpload,
} from "@tabler/icons";
import { showNotification } from "@mantine/notifications";
import { useTranslation } from "react-i18next";
import { useServerApi } from "../../hooks/userServerApi";
import { normalizeReportDownloadUrl } from "../../utils/openReport";
import { FormSectionCard } from "../../layout/page";

function errorMessage(api, error, fallback) {
  return api.getErrorMsg?.(error?.error) || error?.message || fallback;
}

function PlaceholderList({ profile, api, t }) {
  const [placeholders, setPlaceholders] = useState([]);

  useEffect(() => {
    let cancelled = false;
    if (!profile) {
      setPlaceholders([]);
      return undefined;
    }
    api.customDocument.placeholders(profile).then((result) => {
      if (!cancelled && result?.success) {
        setPlaceholders(result.data?.placeholders || []);
      }
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile]);

  if (!profile) return null;

  return (
    <Accordion variant="contained">
      <Accordion.Item value="placeholders">
        <Accordion.Control>{t("Available placeholders")}</Accordion.Control>
        <Accordion.Panel>
          <Stack spacing={4}>
            {placeholders.map((item) => (
              <Text key={item.tag} size="xs">
                <Text span ff="monospace">
                  {item.tag}
                </Text>
                {" — "}
                {item.description}
              </Text>
            ))}
            {!placeholders.length && (
              <Text size="xs" color="dimmed">
                {t("No placeholders for this profile.")}
              </Text>
            )}
          </Stack>
        </Accordion.Panel>
      </Accordion.Item>
    </Accordion>
  );
}

export function useCustomDocumentList() {
  const [api] = useServerApi();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      setLoading(true);
      const listed = await api.customDocument.list();
      if (listed?.success) setDocuments(listed.data?.documents || []);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const upsert = (saved) => {
    setDocuments((current) => {
      const exists = current.some((doc) => doc._id === saved._id);
      const next = exists
        ? current.map((doc) => (doc._id === saved._id ? { ...doc, ...saved } : doc))
        : [...current, saved];
      return next.sort((a, b) => String(a.name).localeCompare(String(b.name)));
    });
  };

  const remove = (id) => {
    setDocuments((current) => current.filter((doc) => doc._id !== id));
  };

  return { documents, loading, upsert, remove };
}

export function CustomDocumentEditor({ record, onCreated, onUpdated, onDeleted }) {
  const [api] = useServerApi();
  const { t } = useTranslation();
  const [entities, setEntities] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);

  const profilesFor = (entity) => profiles.filter((item) => item.entity === entity);
  const entityOptions = entities.map((item) => ({ value: item.id, label: item.label }));
  const draftProfiles = useMemo(
    () => profiles.filter((item) => item.entity === draft?.entity),
    [profiles, draft?.entity]
  );

  useEffect(() => {
    let cancelled = false;
    const blank = (nextEntities, nextProfiles) => {
      const entity = nextEntities[0]?.id || "debitNote";
      const entityProfiles = nextProfiles.filter((item) => item.entity === entity);
      return {
        name: "",
        entity,
        profile: entityProfiles[0]?.id || "",
        active: true,
      };
    };
    api.customDocument
      .profiles()
      .then((catalog) => {
        if (cancelled) return;
        const nextEntities = catalog?.success ? catalog.data?.entities || [] : [];
        const nextProfiles = catalog?.success ? catalog.data?.profiles || [] : [];
        setEntities(nextEntities);
        setProfiles(nextProfiles);
        setDraft(record ? { ...record } : blank(nextEntities, nextProfiles));
      })
      .catch(() => {
        if (!cancelled) setDraft(record ? { ...record } : blank([], []));
      });
    return () => {
      cancelled = true;
    };
    // Mounted per document via key
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const notify = (title, message, color) => {
    showNotification({
      title,
      message,
      color,
      icon:
        color === "green" ? (
          <IconCircleCheck size={18} />
        ) : (
          <IconExclamationMark size={18} />
        ),
    });
  };

  const setDraftEntity = (entity) => {
    const nextProfiles = profilesFor(entity);
    setDraft((current) => ({
      ...current,
      entity,
      profile: nextProfiles.some((item) => item.id === current.profile)
        ? current.profile
        : nextProfiles[0]?.id || "",
    }));
  };

  const createDocument = async () => {
    const name = String(draft?.name || "").trim();
    if (!name) {
      notify(t("Custom document"), t("Name is required"), "red");
      return;
    }
    try {
      setSaving(true);
      const result = await api.customDocument.create({
        name,
        entity: draft.entity,
        profile: draft.profile,
        active: draft.active,
      });
      if (!result?.success) throw result;
      setDraft(result.data);
      onCreated?.(result.data);
      notify(t("Custom document"), t("Document created"), "green");
    } catch (error) {
      notify(t("Custom document"), errorMessage(api, error, t("Could not create document")), "red");
    } finally {
      setSaving(false);
    }
  };

  const saveDocument = async () => {
    if (!draft?._id) return;
    const name = String(draft.name || "").trim();
    if (!name) {
      notify(t("Custom document"), t("Name is required"), "red");
      return;
    }
    try {
      setSaving(true);
      const result = await api.customDocument.update(draft._id, {
        name,
        entity: draft.entity,
        profile: draft.profile,
        active: draft.active,
      });
      if (!result?.success) throw result;
      setDraft((current) => ({ ...current, ...result.data }));
      onUpdated?.(result.data);
      notify(t("Save"), t("settings.saved"), "green");
    } catch (error) {
      notify(t("Save"), errorMessage(api, error, t("settings.saveFailed")), "red");
    } finally {
      setSaving(false);
    }
  };

  const removeDocument = async () => {
    if (!draft?._id) return;
    if (!window.confirm(t("Delete this custom document?"))) return;
    try {
      setSaving(true);
      const result = await api.customDocument.remove(draft._id);
      if (!result?.success) throw result;
      notify(t("Delete"), t("Document deleted"), "green");
      onDeleted?.(draft._id);
    } catch (error) {
      notify(t("Delete"), errorMessage(api, error, t("Could not delete document")), "red");
    } finally {
      setSaving(false);
    }
  };

  const uploadTemplate = async (file) => {
    if (!file || !draft?._id) return;
    try {
      setSaving(true);
      const result = await api.customDocument.uploadTemplate(draft._id, file);
      if (!result?.success) throw result;
      setDraft((current) => ({ ...current, ...result.data }));
      onUpdated?.(result.data);
      notify(t("Upload"), t("Template uploaded"), "green");
    } catch (error) {
      notify(t("Upload"), errorMessage(api, error, t("Upload failed")), "red");
    } finally {
      setSaving(false);
    }
  };

  const downloadTemplate = async () => {
    if (!draft?._id) return;
    try {
      const { blob, filename } = await api.customDocument.downloadTemplate(
        draft._id,
        draft.template?.originalName
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (error) {
      notify(t("Download"), error.message || t("Download failed"), "red");
    }
  };

  const previewTemplate = async () => {
    if (!draft?._id) return;
    try {
      setPreviewOpen(true);
      setPreviewing(true);
      setPreviewUrl("");
      const result = await api.customDocument.preview(draft._id);
      if (!result?.success) throw result;
      setPreviewUrl(normalizeReportDownloadUrl(result.data.url));
    } catch (error) {
      notify(t("Preview"), errorMessage(api, error, t("Preview failed")), "red");
    } finally {
      setPreviewing(false);
    }
  };

  if (!draft) return <Loader size="sm" />;

  return (
    <Stack>
      <FormSectionCard>
        <Title order={5}>{draft._id ? draft.name || t("Custom document") : t("New document")}</Title>
        <Text size="sm" color="dimmed" mt="xs">
          {t("Custom documents can be attached to notification and MCA emails.")}
        </Text>
        <TextInput
          mt="sm"
          label={t("Name")}
          value={draft.name || ""}
          onChange={(event) => {
            const name = event.currentTarget.value;
            setDraft((current) => ({ ...current, name }));
          }}
        />
        <Select
          mt="sm"
          label={t("Entity")}
          data={entityOptions}
          value={draft.entity}
          onChange={(entity) => entity && setDraftEntity(entity)}
        />
        {draftProfiles.length > 1 ? (
          <Select
            mt="sm"
            label={t("Placeholder profile")}
            description={t("This decides which fields are filled into the Word file.")}
            data={draftProfiles.map((item) => ({ value: item.id, label: item.label }))}
            value={draft.profile}
            onChange={(profile) =>
              setDraft((current) => ({ ...current, profile: profile || "" }))
            }
          />
        ) : (
          <Text size="xs" color="dimmed" mt="sm">
            {t("Placeholders")}: {draftProfiles[0]?.label || draft.profile || t("None")}
          </Text>
        )}
        <Switch
          mt="sm"
          label={t("Active")}
          checked={draft.active !== false}
          onChange={(event) => {
            const active = event.currentTarget.checked;
            setDraft((current) => ({ ...current, active }));
          }}
        />
        <Group position="apart" mt="md">
          {draft._id ? (
            <Button size="xs" color="red" variant="subtle" disabled={saving} onClick={removeDocument}>
              {t("Delete")}
            </Button>
          ) : (
            <span />
          )}
          <Button size="xs" loading={saving} onClick={draft._id ? saveDocument : createDocument}>
            {draft._id ? t("Save") : t("Create")}
          </Button>
        </Group>
      </FormSectionCard>
      <FormSectionCard>
        <Title order={5}>{t("Word template")}</Title>
        <Text size="sm" mt="xs">
          {draft.template?.isUploaded
            ? draft.template.currentName || draft.template.originalName
            : t("No template")}
        </Text>
        {draft.template?.uploadedAt && (
          <Text size="xs" color="dimmed">
            {t("Uploaded")}: {new Date(draft.template.uploadedAt).toLocaleString()}
          </Text>
        )}
        {!draft._id && (
          <Text size="xs" color="dimmed" mt="xs">
            {t("Create the document before uploading a Word file.")}
          </Text>
        )}
        <Group mt="md">
          <FileButton onChange={uploadTemplate} accept=".docx" disabled={!draft._id}>
            {(props) => (
              <Button
                {...props}
                size="xs"
                loading={saving}
                disabled={!draft._id}
                leftIcon={<IconUpload size={14} />}
              >
                {t("Upload Word template")}
              </Button>
            )}
          </FileButton>
          <Button
            size="xs"
            variant="default"
            leftIcon={<IconDownload size={14} />}
            disabled={!draft.template?.isUploaded}
            onClick={downloadTemplate}
          >
            {t("Download current")}
          </Button>
          <Button
            size="xs"
            variant="light"
            loading={previewing}
            disabled={!draft.template?.isUploaded}
            onClick={previewTemplate}
          >
            {t("Preview")}
          </Button>
        </Group>
        <Box mt="md">
          <PlaceholderList profile={draft.profile} api={api} t={t} />
        </Box>
      </FormSectionCard>
      <Drawer
        opened={previewOpen}
        onClose={() => {
          setPreviewOpen(false);
          setPreviewUrl("");
        }}
        title={t("Sample preview")}
        padding="md"
        size={960}
        position="right"
      >
        <Stack spacing="sm" sx={{ height: "calc(100vh - 80px)" }}>
          <Text size="sm" color="dimmed">
            {t("Filled sample from dummy data")}
          </Text>
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              border: "1px solid var(--mantine-color-dark-4)",
              borderRadius: 8,
              overflow: "hidden",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "var(--mantine-color-dark-7)",
            }}
          >
            {previewing && !previewUrl && <Loader />}
            {previewUrl && (
              <iframe
                title="custom-document-preview"
                src={`${previewUrl}#navpanes=0&toolbar=1&view=FitH`}
                style={{ width: "100%", height: "100%", border: 0, display: "block" }}
              />
            )}
          </Box>
        </Stack>
      </Drawer>
    </Stack>
  );
}
